/**
 * useSessionRefresh
 *
 * Keeps the access token alive by proactively refreshing it shortly before it
 * expires. The backend's POST /api/v1/auth/refresh requires a still-valid
 * token (it re-issues from the current session), so we must refresh BEFORE
 * expiry — a reactive refresh after a 401 would already be too late.
 *
 * Decodes the JWT `exp`, schedules a refresh ~2 minutes before it, and
 * re-schedules after each refresh. If a refresh fails, we stop and let the
 * next 401 (handled in useApi) sign the user out. Mount once, at the app root.
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/base/stores/authStore';

/** Decode a JWT payload without verifying — we only need the `exp` claim. */
function decodeExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

const SKEW_MS = 2 * 60 * 1000; // refresh 2 min before expiry
const MIN_DELAY_MS = 5 * 1000; // never busy-loop

export function useSessionRefresh(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const exp = decodeExp(accessToken);
    if (!exp) return;

    const msUntilRefresh = exp * 1000 - Date.now() - SKEW_MS;
    const delay = Math.max(MIN_DELAY_MS, msUntilRefresh);

    const timer = window.setTimeout(() => {
      // refreshSession updates the store (accessToken), which re-runs this
      // effect and schedules the next refresh. On failure it returns null and
      // we simply stop; the next protected request 401s and useApi signs out.
      void useAuthStore.getState().refreshSession();
    }, delay);

    // When the user returns to an idle tab, the scheduled timer may not have
    // fired reliably (background tabs throttle timers) so the token can already
    // be expired. Refresh eagerly on focus/visibility if we're inside the SKEW
    // window or already past expiry. The backend now accepts a recently-expired
    // token on /refresh (grace window), so this recovers the session instead of
    // forcing a re-login.
    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      const current = useAuthStore.getState().accessToken;
      const currentExp = current ? decodeExp(current) : null;
      if (currentExp == null) return;
      if (currentExp * 1000 - Date.now() <= SKEW_MS) {
        void useAuthStore.getState().refreshSession();
      }
    };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [accessToken, isAuthenticated]);
}

export default useSessionRefresh;